import { ICategory } from "analytics/amplitude/amplitudeEvents";
import { getProductAttributesApi } from "api/analytics/getProductAttributesApi";
import { getVehicleInfoApi } from "api/analytics/getVehicleInfoApi";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { getShareShoppingCartApi, shareApi } from "api/shopping-cart/shareApi";
import { IVehicle } from "interfaces/session/IVehicle";
import { IShoppingCartStateReducer } from "reducer/shopping-cart.reducer";
import { getStore } from "store";
import { getAbsoluteImagePath } from "util/helper/getAbsoluteImagePath";
import { validateVehicle } from "util/vehicle/validate-vehicle";

type IKlaviyoEventName = "Viewed Product" | "trackViewedItem" | "Added to Cart" | "Started Checkout" |
 "Viewed Category" | "Vehicle Selected" | "Share Cart";

class KlaviyoTrackingService {
  static trackKlaviyoEvent(eventName: IKlaviyoEventName, payload: any, trackDirectly = false) {
    const { authenticationState } = getStore().getState();
    const { businessCustomer, refreshStatus } = authenticationState;
    const userLanguage = window.location.pathname.includes("/fr") ? "fr-ca" : "en-ca";

    if (businessCustomer) {
      return;
    }
    if (!window.klaviyo || refreshStatus !== "complete") {
      setTimeout(() => {
        this.trackKlaviyoEvent(eventName, payload);
      }, 500);
      return;
    }
    if (trackDirectly) {
      window.klaviyo.push([eventName, { ...payload, userLanguage }]);
    } else {
      window.klaviyo.track(eventName, { ...payload, userLanguage });
    }
  }

  static getProductImage(product: ICompactProduct) {
    const { thumbnailV2 } = product;
    if (!thumbnailV2) {
      return "";
    }
    return `https://partsavatar.ca/img/cth/${thumbnailV2}`;
  }

  static getModifiedProduct(product: ICompactProduct) {
    const {
      longDescription, sku, enCategory, canonicalUrl, manufacturer, price, wasPrice, corePrice, partNumber,
    } = product;
    return {
      ...product,
      ...this.getProductHighlights(product),
      ProductName: longDescription,
      ProductID: sku,
      SKU: sku,
      Categories: enCategory,
      ImageURL: this.getProductImage(product),
      URL: `https://partsavatar.ca${canonicalUrl}`,
      Brand: manufacturer,
      Price: price / 100,
      CompareAtPrice: wasPrice / 100,
      price: price / 100,
      wasPrice: wasPrice / 100,
      corePrice: corePrice / 100,
      partnumber: partNumber,
    };
  }

  static getCartItemsPayload(shoppingCart: IShoppingCartStateReducer, productAttributesMap) {
    return shoppingCart.availableItems.map((item) => ({
      ...item,
      ProductID: item.sku,
      SKU: item.sku,
      ProductName: item.longDescription,
      Quantity: item.quantity,
      ItemPrice: item.price / 100,
      RowTotal: (item.price * item.quantity) / 100,
      ProductURL: `https://partsavatar.ca${item.canonicalUrl}`,
      ImageURL: this.getProductImage(item),
      ProductCategories: [item.category.tier0, item.category.tier1, item.category.tier2, item.category.tier3],
      price: item.price / 100,
      wasPrice: item.wasPrice / 100,
      corePrice: item.corePrice / 100,
      afterDiscountPrice: item.afterDiscountPrice / 100,
      discount: item.discount / 100,
      partnumber: item.partNumber,
      ...productAttributesMap[item.sku],
    }));
  }

  static getProductHighlights(product: ICompactProduct) {
    const { productHighlights } = product;
    return Object.fromEntries(productHighlights.map((item) => [item.name, item.value]));
  }

  static async getShareShoppingCartUrl() {
    const { data } = await shareApi();
    return `${window.location.origin}/share/${data.uuid}`;
  }

  static async getProductAttributes(skus: string[]) {
    const { data } = await getProductAttributesApi(skus);
    const productAttributesMap = data.productAttributes.reduce((acc, curr) => {
      acc[curr.sku] = {
        productGrade: curr.productGrade,
        driverProfile: curr.driverProfile,
      };
      return acc;
    }, {});
    return productAttributesMap;
  }

  static identifyKlaviyoUser(email: string) {
    if (!window.klaviyo) {
      return;
    }
    window.klaviyo.identify({
      email,
    });
  }

  static async productViewed(product: ICompactProduct) {
    const productAttributesMap = await this.getProductAttributes([product.sku]);
    const productAttributes = productAttributesMap[product.sku];
    const modifiedProduct = this.getModifiedProduct({ ...product, ...productAttributes });
    this.trackKlaviyoEvent("Viewed Product", modifiedProduct);
    this.trackProductViewed(product, productAttributesMap);
  }

  static async trackProductViewed(product: ICompactProduct, attributesMap?: any) {
    const {
      longDescription, sku, enCategory, canonicalUrl, manufacturer, price, wasPrice, corePrice, partNumber,
    } = product;
    const productAttributesMap = attributesMap || await this.getProductAttributes([product.sku]);
    const productAttributes = productAttributesMap[product.sku];
    this.trackKlaviyoEvent("trackViewedItem", {
      ...product,
      ...productAttributes,
      ...this.getProductHighlights(product),
      Title: longDescription,
      ItemId: sku,
      Categories: enCategory,
      ImageUrl: this.getProductImage(product),
      Url: `https://partsavatar.ca${canonicalUrl}`,
      Metadata: {
        Brand: manufacturer,
        Price: price / 100,
        CompareAtPrice: wasPrice / 100,
      },
      price: price / 100,
      wasPrice: wasPrice / 100,
      corePrice: corePrice / 100,
      partnumber: partNumber,
    });
  }

  static async addedToCart(product: ICompactProduct, shoppingCart: IShoppingCartStateReducer) {
    const {
      price, longDescription, sku, enCategory, canonicalUrl, corePrice,
    } = product;
    const { totalCost } = shoppingCart;
    const shareShoppingCartUrl = await this.getShareShoppingCartUrl();

    const skusInCart = shoppingCart.availableItems.map((item) => item.sku);
    const productAttributesMap = await this.getProductAttributes(skusInCart);

    this.trackKlaviyoEvent("Added to Cart", {
      $value: totalCost / 100,
      AddedItemProductName: longDescription,
      AddedItemProductID: sku,
      AddedItemSKU: sku,
      AddedItemCategories: [enCategory.tier0, enCategory.tier1, enCategory.tier2, enCategory.tier3],
      AddedItemImageURL: this.getProductImage(product),
      AddedItemURL: `https://partsavatar.ca${canonicalUrl}`,
      AddedItemPrice: price / 100,
      AddedItemQuantity: shoppingCart.availableItems.find((item) => item.sku === product.sku)?.quantity,
      ItemNames: [longDescription],
      CheckoutURL: shareShoppingCartUrl,
      Items: this.getCartItemsPayload(shoppingCart, productAttributesMap),
      ...(corePrice && { corePrice: corePrice / 100 }),
      ...productAttributesMap[product.sku],
      discountAmount: shoppingCart.subtotalWithDiscountDetails.appliedDiscountsCost,
      discountCampaign: shoppingCart.subtotalWithDiscountDetails.discountsWithType.map((discount) => (discount.readableDiscountName)),
    });
  }

  static async startedCheckout(shoppingCart: IShoppingCartStateReducer) {
    const {
      availableItems, totalCost, city, zipCode, province, selectedDeliveryMethod,
    } = shoppingCart;
    const skusInCart = shoppingCart.availableItems.map((item) => item.sku);
    const productAttributesMap = await this.getProductAttributes(skusInCart);
    const cartItems = this.getCartItemsPayload(shoppingCart, productAttributesMap);
    const shareShoppingCartUrl = await this.getShareShoppingCartUrl();
    const tier0s: string[] = []; const tier1s: string[] = []; const tier2s: string[] = []; const
      tier3s: string[] = [];
    const vehicleMakes: string[] = []; const vehicleModels: string[] = []; const
      vehicleYears: string[] = [];
    availableItems.forEach((item) => {
      const {
        tier0, tier1, tier2, tier3,
      } = item.category;
      const vehicle = item.vehicles[0];
      tier0s.push(tier0);
      tier1s.push(tier1);
      tier2s.push(tier2);
      tier3s.push(tier3);
      if (vehicle) {
        vehicleMakes.push(vehicle.make);
        vehicleModels.push(vehicle.model);
        vehicleYears.push(vehicle.year);
      }
    });

    this.trackKlaviyoEvent("Started Checkout", {
      $value: totalCost / 100,
      ItemNames: availableItems.map((item) => item.longDescription),
      CheckoutURL: shareShoppingCartUrl,
      Items: cartItems,
      totalCost: totalCost / 100,
      tier0s,
      tier1s,
      tier2s,
      tier3s,
      city,
      postalCode: zipCode,
      province,
      selectedDeliveryMethod,
      vehicleMakes,
      vehicleModels,
      vehicleYears,
      discountAmount: shoppingCart.subtotalWithDiscountDetails.appliedDiscountsCost / 100,
      discountCampaign: shoppingCart.subtotalWithDiscountDetails.discountsWithType.map((discount) => (discount.readableDiscountName)),
      productGrade: cartItems.map((item) => item.productGrade),
      driverProfile: cartItems.map((item) => item.driverProfile),
    });
  }

  static async categoryViewed(category: ICategory, vehicle: IVehicle, categoryUrl: string, products) {
    const {
      tier0, tier1, tier2, tier3,
    } = category;
    const topProductSkus = products.slice(0, 6).map((product) => product.sku);
    const productAttributesMap = await this.getProductAttributes(topProductSkus);
    const topProducts = products.slice(0, 6).map((product) => this.getModifiedProduct({ ...product, ...productAttributesMap[product.sku] }));
    let payload = {
      tier0,
      tier1,
      tier2,
      tier3,
      categoryName: tier3 || tier2 || tier1 || tier0,
      categoryUrl: getAbsoluteImagePath(categoryUrl),
      categoryItems: topProducts,
    };
    let categoryTier = 0;
    if (tier3) {
      categoryTier = 3;
    } else if (tier2) {
      categoryTier = 2;
    } else if (tier1) {
      categoryTier = 1;
    }

    payload.categoryTier = categoryTier;
    if (validateVehicle(vehicle)) {
      const { data: vehicleData } = await getVehicleInfoApi(vehicle.make, vehicle.model, vehicle.year);
      const { year, make, model } = vehicleData;
      payload = { ...payload, ...vehicleData, categoryName: `${year} ${make} ${model} ${payload.categoryName}` };
    }
    this.trackKlaviyoEvent("Viewed Category", payload);
  }

  static async vehicleSelectedEvent(vehicle: IVehicle) {
    const { data } = await getVehicleInfoApi(vehicle.make, vehicle.model, vehicle.year);
    this.trackKlaviyoEvent("Vehicle Selected", data);
  }

  static async shareCartEvent(cartUrl: string, associateName: string, associatePic: string) {
    const [associateFirstName = "", associateLastName = ""] = (associateName ?? "").split(" ");
    const shareCartUrl = cartUrl.split("/share/")[1];
    const shareCartUuid = shareCartUrl?.split("?")[0];
    const { data: shoppingCart } = await getShareShoppingCartApi(shareCartUuid);
    const {
      availableItems, totalCost,
    } = shoppingCart;
    const skusInCart = shoppingCart.availableItems.map((item) => item.sku);
    const productAttributesMap = await this.getProductAttributes(skusInCart);
    const cartItems = this.getCartItemsPayload(shoppingCart, productAttributesMap);
    const tier0s: string[] = []; const tier1s: string[] = []; const tier2s: string[] = []; const
      tier3s: string[] = [];
    const vehicleMakes: string[] = []; const vehicleModels: string[] = []; const
      vehicleYears: string[] = [];
    availableItems.forEach((item) => {
      const {
        tier0, tier1, tier2, tier3,
      } = item.category;
      const vehicle = item.vehicles[0];
      tier0s.push(tier0);
      tier1s.push(tier1);
      tier2s.push(tier2);
      tier3s.push(tier3);
      if (vehicle) {
        vehicleMakes.push(vehicle.make);
        vehicleModels.push(vehicle.model);
        vehicleYears.push(vehicle.year);
      }
    });
    this.trackKlaviyoEvent("Share Cart", {
      cartUrl,
      $value: totalCost / 100,
      ItemNames: availableItems.map((item) => item.longDescription),
      CheckoutURL: cartUrl,
      Items: cartItems,
      totalCost: totalCost / 100,
      tier0s,
      tier1s,
      tier2s,
      tier3s,
      vehicleMakes,
      vehicleModels,
      vehicleYears,
      productGrade: cartItems.map((item) => item.productGrade),
      driverProfile: cartItems.map((item) => item.driverProfile),
      associateFirstName,
      associateLastName,
      associatePic,
    });
  }
}

export default KlaviyoTrackingService;
